import { useState, useEffect } from 'react';
import {
  saveCurrentProject,
  getCurrentProject,
  removeCurrentProject,
} from '../utils/localStorage';

interface Project {
  id: number;
  projectName: string;
}

interface ProjectList {
  status: string;
  data: Project[];
}

interface CreateProjectResponse {
  status: string;
  message?: string;
  data?: {
    id: number;
    project_name: string;
  };
}

interface DeleteProjectResponse {
  status: string;
  message?: string;
}

export const useProject = (tenantId?: number) => {
  const [projectName, setProjectName] = useState<string>('');
  const [projectList, setProjectList] = useState<Project[]>([]);
  const [selectedProjectName, setSelectedProjectName] = useState<string>();
  const [isProjectListLoaded, setIsProjectListLoaded] = useState(false);

  const fetchProjects = async () => {
    if (!tenantId) {
      setIsProjectListLoaded(false);
      return;
    }

    setIsProjectListLoaded(false);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/get-project-list`, {
        credentials: 'include',
      });
      const data: ProjectList = await res.json();

      if (!data?.data || data.data.length === 0) {
        console.warn('広告主データが空です');
        return;
      }

      setProjectList(data.data);

      const project = getCurrentProject();
      const selectableProjects = data.data.filter((item) => item.id !== -1);
      const currentProject = selectableProjects.find((item) => item.id === project?.id);

      if (currentProject) {
        setSelectedProjectName(currentProject.projectName);
      } else if (selectableProjects.length > 0) {
        const firstProject = selectableProjects[0];
        saveCurrentProject(firstProject.id, firstProject.projectName);
        setSelectedProjectName(firstProject.projectName);
      } else {
        removeCurrentProject();
        setSelectedProjectName(undefined);
      }
    } catch (err) {
      console.error('広告主取得エラー:', err);
    } finally {
      setIsProjectListLoaded(true);
    }
  };

  useEffect(() => {
    if (tenantId) {
      fetchProjects();
    }
  }, [tenantId]);

  const createProject = async (name: string): Promise<CreateProjectResponse | undefined> => {
    if (!name.trim()) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/save-project`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_name: name.trim() }),
      });
      const data: CreateProjectResponse = await res.json();

      if (data.status === 'success') {
        await fetchProjects();
      } else {
        alert(`登録に失敗しました：${data.message}`);
      }
      return data;
    } catch (error) {
      console.error('送信エラー:', error);
    }
  };

  const deleteProject = async (projectId: number): Promise<boolean> => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/delete-project`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId }),
      });
      const data: DeleteProjectResponse = await res.json();
      return res.ok && data.status === 'success';
    } catch (error) {
      console.error('プロジェクト削除エラー:', error);
      return false;
    }
  };

  const handleProjectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    return createProject(projectName);
  };

  return {
    projectList,
    selectedProjectName,
    fetchProjects,
    setSelectedProjectName,
    projectName,
    setProjectName,
    createProject,
    deleteProject,
    handleProjectSubmit,
    isProjectListLoaded,
  };
};
